import { useEffect, useState } from 'react'

export const USERCENTRICS_CONSENT_CHANGED_MESSAGE =
  'USERCENTRICS_CONSENT_CHANGED'
export const REQUEST_CONSENT_MESSAGE = 'REQUEST_USERCENTRICS_CONSENT'

export type UcServiceSnapshot = { id: string; name: string; consent: boolean }

type UsercentricsConsent = boolean | { status?: boolean }

type UsercentricsService = {
  id?: string
  name?: string
  consent?: UsercentricsConsent
}

type UsercentricsUI = {
  getServicesBaseInfo?: () => UsercentricsService[]
  updateServicesConsents?: (
    services: Array<{ id: string; consent: boolean }>
  ) => void
  acceptService?: (serviceId: string) => Promise<void>
  denyService?: (serviceId: string) => Promise<void>
  showSecondLayer?: () => void
}

export type AnalyticsConsent = {
  amplitude: boolean
  googleAnalytics: boolean
  clarity: boolean
}

declare global {
  interface Window {
    UC_UI?: UsercentricsUI
  }
}

const initialConsent: AnalyticsConsent = {
  amplitude: false,
  googleAnalytics: false,
  clarity: false
}

const serviceMatchers: Record<keyof AnalyticsConsent, string[]> = {
  amplitude: ['amplitude'],
  googleAnalytics: ['google analytics', 'google analytics 4'],
  clarity: ['microsoft clarity', 'clarity']
}

const usercentricsEvents = ['UC_UI_INITIALIZED', 'UC_UI_CMP_EVENT']

const getConsentStatus = (consent?: UsercentricsConsent) =>
  typeof consent === 'boolean' ? consent : consent?.status === true

const getServiceName = (service: UsercentricsService) =>
  service.name?.trim().toLowerCase() ?? ''

const isMatchingService = (serviceName: string, names: string[]) =>
  names.some(name => serviceName === name || serviceName.includes(name))

const matchServiceKey = (
  serviceName: string
): keyof AnalyticsConsent | null => {
  for (const [key, names] of Object.entries(serviceMatchers) as [
    keyof AnalyticsConsent,
    string[]
  ][]) {
    if (isMatchingService(serviceName, names)) return key
  }
  return null
}

const getUsercentricsConsent = (): AnalyticsConsent => {
  const services = window.UC_UI?.getServicesBaseInfo?.()
  if (!services?.length) return initialConsent

  const consent = { ...initialConsent }
  services.forEach(service => {
    const serviceName = getServiceName(service)
    if (!serviceName) return
    const key = matchServiceKey(serviceName)
    if (key) consent[key] = getConsentStatus(service.consent)
  })
  return consent
}

const hasConsentChanged = (a: AnalyticsConsent, b: AnalyticsConsent) =>
  a.amplitude !== b.amplitude ||
  a.googleAnalytics !== b.googleAnalytics ||
  a.clarity !== b.clarity

// ucServices carries the full UC services snapshot from the container broadcast.
// When present all services are updated; without it only the 3 tracked ones are.
const syncUcSdkState = (
  broadcastConsent: AnalyticsConsent,
  ucServices?: UcServiceSnapshot[]
) => {
  const ucUI = window.UC_UI
  if (!ucUI) return false

  let updates: UcServiceSnapshot[]

  if (ucServices?.length) {
    updates = ucServices
  } else {
    const services = ucUI.getServicesBaseInfo?.()
    if (!services?.length) return false
    updates = services.flatMap(service => {
      if (!service.id) return []
      const key = matchServiceKey(getServiceName(service))
      if (!key) return []
      return [
        {
          id: service.id,
          name: service.name ?? '(unknown)',
          consent: broadcastConsent[key]
        }
      ]
    })
  }

  if (!updates.length) return false

  if (ucUI.updateServicesConsents) {
    ucUI.updateServicesConsents(updates)
    return true
  }

  if (ucUI.acceptService || ucUI.denyService) {
    updates.forEach(({ id, consent }) => {
      if (consent) ucUI.acceptService?.(id)
      else ucUI.denyService?.(id)
    })
    return true
  }

  return false
}

export const useUsercentricsConsent = () => {
  const [consent, setConsent] = useState<AnalyticsConsent>(initialConsent)

  useEffect(() => {
    const isInIframe = window.parent !== window
    let pending: {
      consent: AnalyticsConsent
      ucServices?: UcServiceSnapshot[]
    } | null = null

    const syncConsent = () => {
      const nextConsent = getUsercentricsConsent()
      setConsent(currentConsent => {
        if (!hasConsentChanged(currentConsent, nextConsent))
          return currentConsent
        return nextConsent
      })
    }

    const applyPendingToSdk = () => {
      if (!pending) return
      syncUcSdkState(pending.consent, pending.ucServices)
    }

    const handleConsentBroadcast = (e: MessageEvent) => {
      if (e.data?.type !== USERCENTRICS_CONSENT_CHANGED_MESSAGE) return
      if (!isInIframe) return
      if (e.source !== window.parent) return

      const nextConsent: AnalyticsConsent = {
        amplitude: !!e.data.consent?.amplitude,
        googleAnalytics: !!e.data.consent?.googleAnalytics,
        clarity: !!e.data.consent?.clarity
      }
      const ucServices = Array.isArray(e.data.ucServices)
        ? (e.data.ucServices as UcServiceSnapshot[])
        : undefined

      setConsent(currentConsent => {
        if (!hasConsentChanged(currentConsent, nextConsent))
          return currentConsent
        return nextConsent
      })

      const applied = syncUcSdkState(nextConsent, ucServices)
      if (!applied) pending = { consent: nextConsent, ucServices }
    }

    syncConsent()

    if (isInIframe) {
      // Container is authoritative for consent in iframes. UC SDK events
      // (UC_UI_CMP_EVENT / UC_UI_INITIALIZED) would override the broadcast
      // with stale SDK state, so we only subscribe to UC_UI_INITIALIZED to
      // push any pending broadcast into the SDK once it loads.
      window.addEventListener('UC_UI_INITIALIZED', applyPendingToSdk)
      window.parent.postMessage(
        { type: REQUEST_CONSENT_MESSAGE },
        window.location.origin
      )
    } else {
      usercentricsEvents.forEach(eventName => {
        window.addEventListener(eventName, syncConsent)
      })
    }

    window.addEventListener('message', handleConsentBroadcast)

    return () => {
      window.removeEventListener('message', handleConsentBroadcast)
      if (isInIframe) {
        window.removeEventListener('UC_UI_INITIALIZED', applyPendingToSdk)
      } else {
        usercentricsEvents.forEach(eventName => {
          window.removeEventListener(eventName, syncConsent)
        })
      }
    }
  }, [])

  return consent
}
